import React from 'react'
import { isStaleChunkError, reloadForStaleChunk } from '../lib/staleChunk'

interface Props {
  children: React.ReactNode
}

interface State {
  error: Error | null
}

/**
 * App-root error boundary: renders a recoverable error screen instead of the
 * blank white page React leaves behind when a render throws (ui #39 — a
 * layout-level component crash used to take down every route at once).
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // A page chunk from before the last deploy: reload into the new build instead
    // of showing the error screen (and filing it in Sentry). See lib/staleChunk.
    if (isStaleChunkError(error) && reloadForStaleChunk()) {
      return
    }
    console.error('Unhandled render error', error, info)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4 bg-gray-950 text-white p-8 text-center">
          <h1 className="text-xl font-bold">Something went wrong</h1>
          <p className="text-sm text-gray-400 max-w-md break-words">
            {this.state.error.message}
          </p>
          <button
            onClick={() => {
              this.setState({ error: null })
              window.location.reload()
            }}
            className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm font-semibold transition-colors"
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
